import React from "react";
import { Section, Text, Link } from "@/components";
import { Paragraph, LineBreak, Quote, SubHeading } from "./Common";

export const PrivacyPolicy = (): JSX.Element => {
  return (
    <div className="h-[calc(100vh-152px)] overflow-y-auto">
      <Section className="mx-auto max-w-[1191px] justify-start">

        <div className="mb-14">
          <Text.Heading className="text-left mb-6">Personal Data Privacy Policy</Text.Heading>
          <Paragraph>
            Updated: March 31st 2025
            <LineBreak />
            This privacy policy ("<Quote>Privacy Policy</Quote>") is issued by <Quote>TWO12 Inc.</Quote>
            ("<Quote>T12</Quote>", "<Quote>we</Quote>", "<Quote>us</Quote>") 
            T12 are committed to protecting your privacy and safeguarding personal data entrusted to us.
            <LineBreak />
            This Privacy Policy applies to personal data collected from users of the IntelliRoom™ platform operated by T12, 
            currently found at <Link to="https://www.intelliroom.ai" className="!p-0"><Quote>www.intelliroom.ai</Quote></Link> (the “<Quote>Platform</Quote>”) and other interactions (e.g., customer service enquiries) 
            that users may have with T12 which result in the collection and processing of personal data (collectively, “<Quote>Services</Quote>”).
          </Paragraph>
        </div>
                
        <div  className="mb-14">
          <SubHeading>1. PURPOSE AND SCOPE</SubHeading>
          <Paragraph>
            This Privacy Policy explains how T12 collects, uses, stores, and protects personal data processed through the Platform. It applies to any personal data:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                Provided directly by users (e.g., during registration or support requests);
              </li>
              <li>
                Contained within uploaded documents;
              </li>
              <li>
                Incidentally included in prompts or queries;
              </li>
              <li>
                Collected automatically through platform use (e.g., IP address, device information).
              </li>
            </ul>
            <LineBreak />
            This Privacy Policy is intended to comply with applicable data protection laws, 
            including the General Data Protection Regulations applicable to the UK and the European Union (GDPR), 
            the California Consumer Privacy Act (CCPA), and the California Privacy Rights Act (CPRA), where applicable.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>2. PERSONAL DATA WE COLLECT</SubHeading>
          <Paragraph>
            When you use our Services, we collect certain types of personal information from various sources, including information you provide directly, information collected automatically, and information obtained from third parties. This includes:
            <LineBreak />
            <ul className="list-disc list-inside ml-12 text-indent-12">
              <li>
                User account information: name, email, password, company affiliation
              </li>
              <li>
                Support and communications data: messages or support queries
              </li>
              <li>
                Usage data: IP address, browser type, access logs
              </li>
              <li>
                Uploaded content: documents and data files may contain personal data
              </li>
              <li>
                Prompt content: personal data may be included in prompts or queries, though T12 does not require or encourage this, and may take technological measures not to process this information
              </li>
              <li>
                Third party providers.
              </li>
            </ul>
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>3. HOW WE USE PERSONAL DATA</SubHeading>
          <Paragraph>
            We use personal data for the following purposes:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                To operate and maintain the Platform
              </li>
              <li>
                To provide customer support
              </li>
              <li>
                To analyse usage and improve performance
              </li>
              <li>
                To meet legal obligations (e.g. recordkeeping, compliance)
              </li>
            </ul>
            <LineBreak />
            We do not use personal data to train language models, or for automated decision-making. 
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>4. LAWFUL BASIS FOR PROCESSING (GDPR)</SubHeading>
          <Paragraph>
            Where GDPR applies, we process personal data on the following legal bases:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                Contract performance (e.g., providing the service)
              </li>
              <li>
                Legitimate interests (e.g., service improvement, security)
              </li>
              <li>
                Consent (e.g., marketing or optional data input)
              </li>
              <li>
                Legal obligation (e.g., responding to lawful requests)
              </li>
            </ul>
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>5.  DATA SHARING AND DISCLOSURE</SubHeading>
          <Paragraph>
            We do not sell personal data. We may share it only:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                With our service providers (e.g., hosting, email, support tools) bound by strict confidentiality undertakings;
              </li>
              <li>
                Where required by law or lawful government request;
              </li>
              <li>
                With the user’s consent.
              </li>
            </ul>

            <LineBreak />

            We use Amazon Web Services to power AI functionality, data processed by models remains within Amazon’s environment and is not used for training. Without limitation, your prompts (inputs) and completions (outputs), your embeddings, and your training data:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                are NOT available to other Amazon customers
              </li>
              <li>
                are NOT used to improve Amazon models
              </li>
              <li>
                are NOT used to train, retrain, or improve foundation models
              </li>
            </ul>
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>6. DATA RETENTION</SubHeading>
          <Paragraph>
            Personal data is retained only as long as necessary for the purposes set out above. Users may request deletion at any time, subject to certain legal or contractual retention obligations.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>7. DATA SUBJECT RIGHTS</SubHeading>
          <Paragraph>
            Depending on your jurisdiction, you may have rights to:
            <LineBreak />
            <ul className="list-disc list-inside ml-12">
              <li>
                Access, correct, or delete your personal data;
              </li>
              <li>
                Object to or restrict certain processing;
              </li>
              <li>
                Port your data to another provider;
              </li>
              <li>
                Complain to a supervisory authority;
              </li>
              <li>
                Know what personal information we collect, use, disclose, and sell;
              </li>
              <li>
                Request deletion of personal information
              </li>
              <li>
                Opt out of the sharing of personal information
              </li>
              <li>
                Limit the use and disclosure of sensitive personal information
              </li>
              <li>
                Not be discriminated against for exercising any CCPA/CPRA rights
              </li>
            </ul>
            <LineBreak />
            Requests can be submitted by contacting support@intelliroom.ai. We will verify your request using the information associated with your account, and may request additional information as necessary.
            Nothing in this Privacy Policy shall adversely affect your statutory rights as a data subject.
          </Paragraph>
        </div>
        
        <div  className="mb-14">
          <SubHeading>8. DATA SECURITY</SubHeading>
          <Paragraph>
            We apply appropriate technical and organisational measures to protect personal data, including encryption, access controls, and monitoring. However, no method of transmission or storage is completely secure.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>9. CHILDREN’S PRIVACY</SubHeading>
          <Paragraph>
            The Services are not intended for individuals under the age of 13. We do not knowingly collect personal data from children.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>10. CHANGES TO THIS PRIVACY POLICY</SubHeading>
          <Paragraph>
            We may update this Privacy Policy from time to time. Changes will be posted on the Platform with a revised effective date.
          </Paragraph>
        </div>

        <div  className="mb-14">
          <SubHeading>11. CONTACT</SubHeading>
          <Paragraph>
            For questions or data protection requests, please contact:<br />
            <Link to="mailto:support@intelliroom.ai" className="!p-0"><Quote>support@intelliroom.ai</Quote></Link>
          </Paragraph>
        </div>
      </Section>
    </div>
  );
};
